import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouteProps } from 'react-router';
import { Redirect } from 'react-router-dom';
import build from 'redux-object';
import styled from 'styled-components';
import { Color } from 'core';
import UserSessionJourneyCard from 'learn/components/UserSessionJourneyCard';
import LearnHeader from 'learn/components/LearnHeader';
import { ISet } from 'models/set';
import { handleGetSet } from 'store/set';
import { HttpStatusCodes } from 'utils/httpStatusCodes';
import { postMessageExitLearnApp } from 'utils/iframeMessageUtils';
import { locationState, queryParams, redirectToV3 } from 'utils/pathUtils';
import { getBtnText, getMessaging } from 'utils/userSessionJourneyMessaging';
import { useTranslation } from 'react-i18next';

interface IOwnProps extends RouteProps {
  match: { params: { id: string } };
}

const Container = styled.div`
  background-color: ${Color.primaryGray};
  display: flex;
  flex-direction: column;
  height: 100vh;
`;

export const UserSessionJourneyPage: React.FC<IOwnProps> = ({ match, location }) => {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();
  const setId = match.params.id;

  const set: ISet = useSelector((state: any) => build(state.data, 'sets', setId));
  const memoryAggregate = useSelector((state: any) => {
    const s = build(state.data, 'sets', setId);
    return s && s.meta && s.meta.memoryAggregate;
  });

  const [toStudySession, setToStudySession] = useState(false);
  const [messaging, setMessaging] = useState<any>(null);
  const [btnText, setBtnText] = useState<any>(null);
  const [studyType, setStudyType] = useState<'force_review' | 'recommended'>('recommended');
  const [hasValidatedSetAccess, setHasValidatedSetAccess] = useState(false);

  const mainFocusRef = useRef<any>(null);

  const returnUrl = queryParams(location).returnUrl || locationState(location).returnUrl;
  const isLTI = !!queryParams(location).lti || !!locationState(location).lti;
  const isAssessment = set?.goalType === 'assessment';
  const isSurvey = set?.goalType === 'survey';

  useEffect(() => {
    const handleMessageReceived = (event) => {
      if (event.data === 'skipToMain') {
        mainFocusRef.current?.focus();
      }
    };
    window.addEventListener('message', handleMessageReceived, true);
    return () => {
      window.removeEventListener('message', handleMessageReceived);
    };
  }, []);

  useEffect(() => {
    setHasValidatedSetAccess(false);
    setMessaging(null);
    setBtnText(null);

    dispatch(handleGetSet(setId)).then((response) => {
      if (response.error?.status === HttpStatusCodes.Forbidden && isLTI) {
        window.location.href = '/';
        return;
      }

      setHasValidatedSetAccess(true);
    });
  }, [dispatch, setId, isLTI]);

  useEffect(() => {
    if (!hasValidatedSetAccess || !memoryAggregate || !set) return;

    const justStudied = location?.state && (location.state as any).justStudied;
    const { unstartedItemsCount, studiedItemsCount, percentCorrect, eligibleItemsCount, progress, seeNextAt } =
      memoryAggregate;

    setMessaging(
      getMessaging(
        t,
        studiedItemsCount,
        unstartedItemsCount,
        percentCorrect,
        eligibleItemsCount,
        progress,
        justStudied,
        seeNextAt,
        isAssessment,
        isSurvey
      )
    );
    setBtnText(
      getBtnText(t, unstartedItemsCount, eligibleItemsCount, progress, justStudied, isAssessment, isSurvey)
    );
  }, [hasValidatedSetAccess, memoryAggregate, set, t, location, isAssessment, isSurvey]);

  const exit = useCallback(() => {
    postMessageExitLearnApp();
    redirectToV3(returnUrl);
  }, [returnUrl]);

  const launchStudySession = useCallback((type: 'force_review' | 'recommended') => {
    setStudyType(type);
    setToStudySession(true);
  }, []);

  const isGoodForNow = useCallback(() => {
    if (!memoryAggregate) return false;
    const { unstartedItemsCount, eligibleItemsCount } = memoryAggregate;
    return unstartedItemsCount === 0 && (eligibleItemsCount === 0 || isAssessment || isSurvey);
  }, [memoryAggregate, isAssessment, isSurvey]);

  const showPrimaryButton = useCallback(() => {
    if (isGoodForNow()) {
      return !isLTI;
    }
    return true;
  }, [isGoodForNow, isLTI]);

  const handlePrimaryBtnClick = useCallback(() => {
    if (isGoodForNow()) {
      exit();
    } else {
      launchStudySession('recommended');
    }
  }, [isGoodForNow, exit, launchStudySession]);

  const handleSecondaryBtnClick = useCallback(() => {
    launchStudySession('force_review');
  }, [launchStudySession]);

  if (toStudySession) {
    const to = {
      pathname: `/learn/sets/${setId}`,
      search: `?studyType=${studyType}`,
      state: { returnUrl, lti: isLTI },
    };
    return <Redirect to={to} />;
  }

  var title = t('Learn');
  if (set?.name) {
    title += ' | ' + set.name;
  }
  document.title = title + ' | Cerego';

  return (
    <Container className="UserSessionJourneyPage">
      <LearnHeader exit={exit} set={set} showExit={!isLTI} />
      {messaging && (
        <UserSessionJourneyCard
          ref={mainFocusRef}
          messaging={messaging}
          btnText={btnText}
          showPrimaryButton={showPrimaryButton()}
          progress={memoryAggregate.progress}
          handlePrimaryBtnClick={handlePrimaryBtnClick}
          handleSecondaryBtnClick={handleSecondaryBtnClick}
          showDetails={!(isAssessment || isSurvey)}
        />
      )}
    </Container>
  );
};

export default UserSessionJourneyPage;
